import * as DateIso from "@tiicker/util/lib/date-iso";
import { GetIexNews, IexNews } from "$gql/queries/general/GetIexNews.gen";
import SidebarResult from "components/core/Sidebar/SidebarResult";
import SidebarContainer from "components/core/SidebarContainer/SidebarContainer";
import LoadingBlock from "components/Loading/LoadingBlock";
import { useQueryBundle } from "source/hooks";
import styles from "./Sidebar.module.scss";
import PushPinIcon from "public/icons/push-pin.svg";
import { PinnedNews } from "@tiicker/util/lib/contentful/types";
import { Asset, Entry } from "contentful";
import { getImageReference } from "@tiicker/util/lib/contentful/helpers";
import TiiDisplay from "components/TiiDisplay/TiiDisplay";
import { useEffect, useState } from "react";
import Button from "components/core/Button/Button";

interface Props {
  isOpen?: boolean;
  onSidebarClose: (boolean) => void;
  tickerSymbol: string;
  extractedPinnedNews?: Entry<PinnedNews>[];
  includes?: { Asset: Asset[] };
}

const Sidebar = (props: Props) => {
  const [newsCount, setNewsCount] = useState<number>(
    props.extractedPinnedNews ? 20 - props.extractedPinnedNews.length : 20
  );
  const [iexNewsItems, setIexNewsItems] = useState<IexNews[]>([]);

  const newsQuery = useQueryBundle(GetIexNews, {
    variables: {
      ticker: props.tickerSymbol.toLowerCase(),
      count: newsCount,
    },
  });

  useEffect(() => {
    if (newsQuery.state === "DONE") {
      setIexNewsItems(
        iexNewsItems.concat(
          (newsQuery.data.iex.news ?? []).filter(
            (news) => !iexNewsItems.some((x) => x.id === news.id)
          )
        )
      );
    }
  }, [newsQuery.state]);

  if (newsQuery.state !== "DONE" && iexNewsItems.length === 0) {
    return (
      <SidebarContainer {...props}>
        <div className={styles.Sidebar__wrapper}>
          <LoadingBlock />
        </div>
      </SidebarContainer>
    );
  }

  const loadMore = () => {
    setNewsCount(newsCount + 10);
  };

  return (
    <SidebarContainer {...props}>
      <div className={styles.Sidebar__wrapper}>
        <h5 className={styles.Sidebar__header}>
          <PushPinIcon className={styles.Sidebar__icon} />
          Latest news for{" "}
          {props.tickerSymbol !== "" ? (
            <TiiDisplay
              className={styles.titleTii}
              size="mdsm"
              type="dark"
              text={props.tickerSymbol.toUpperCase()}
            />
          ) : (
            "You"
          )}
        </h5>
        <div className={styles.Sidebar__grid}>
          {props.extractedPinnedNews &&
            props.includes &&
            props.extractedPinnedNews.map((pinnedNews) => {
              const date = DateIso.formatLongMonthDayYear(
                DateIso.toIsoDate(pinnedNews.sys.createdAt)
              );
              const image = getImageReference(
                pinnedNews.fields?.thumbnail,
                props.includes
              );

              return (
                <SidebarResult
                  key={pinnedNews.sys.id}
                  title={pinnedNews.fields.title}
                  subTitle={date}
                  url={pinnedNews.fields.url}
                  backgroundImage={image?.fields.file.url}
                  sidebarOpened={props.isOpen}
                />
              );
            })}
          {iexNewsItems &&
            iexNewsItems.map((newsItem) => {
              const formattedDate = DateIso.formatLongMonthDayYear(
                DateIso.toIsoDate(newsItem.datetime)
              );
              return (
                <SidebarResult
                  key={`newsSidebar-${newsItem.id}`}
                  backgroundImage={newsItem.image || undefined}
                  title={newsItem.headline}
                  subTitle={formattedDate}
                  url={newsItem.url}
                  sidebarOpened={props.isOpen}
                  source={newsItem.source}
                />
              );
            })}
          <div className={styles.Load__more__container}>
            <Button onClick={loadMore}>Load More</Button>
          </div>
        </div>
      </div>
    </SidebarContainer>
  );
};

export default Sidebar;
